<script lang="ts" setup>
import { computed } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import { useI18n } from 'vue-i18n';
import SectionHeader from '@/components/SectionHeader.vue';
import { getCustomIcon } from '@/common/custom-icons.ts';
import type { GetProjectDetailsProjectResponse } from '@/api';
import { faBath, faBed, faRulerTriangle, faToilet } from '@fortawesome/pro-solid-svg-icons';
import { getBedroomsLabel } from '@/views/projects/bedrooms.ts';

const props = defineProps<{
  project: GetProjectDetailsProjectResponse;
  modelId: number;
}>();

const { t } = useI18n();

const currentModel = computed(() => {
  const model = props.project.models.find((model) => model.id === props.modelId);
  if (!model) {
    throw new Error('Model not found.');
  }
  return model;
});
</script>

<template>
  <section v-if="currentModel.bedrooms !== null || currentModel.bathrooms > 0 || currentModel.halfBathrooms > 0 || currentModel.floorArea > 0 || currentModel.tags.length > 0">
    <SectionHeader>{{ t('views.project.characteristics.header') }}</SectionHeader>
    <div>
      <ul class="2xs:grid-cols-2 grid grid-cols-1 gap-4 text-sm/4 text-gray-700 sm:grid-cols-3 md:text-base/5 lg:text-lg/6">
        <li v-if="currentModel.bedrooms !== null">
          <span class="mr-2 inline-flex h-4 w-4 items-center md:h-5 md:w-5 lg:h-6 lg:w-6">
            <FontAwesomeIcon :icon="faBed" />
          </span>
          {{ getBedroomsLabel(t, currentModel) }}
        </li>
        <li v-if="currentModel.bathrooms > 0">
          <span class="mr-2 inline-flex h-4 w-4 items-center md:h-5 md:w-5 lg:h-6 lg:w-6">
            <FontAwesomeIcon :icon="faBath" />
          </span>
          {{ t('views.project.characteristics.bathrooms', currentModel.bathrooms) }}
        </li>
        <li v-if="currentModel.halfBathrooms > 0">
          <span class="mr-2 inline-flex h-4 w-4 items-center md:h-5 md:w-5 lg:h-6 lg:w-6">
            <FontAwesomeIcon :icon="faToilet" />
          </span>
          {{ t('views.project.characteristics.half-bathrooms', currentModel.halfBathrooms) }}
        </li>
        <li v-if="currentModel.floorArea > 0">
          <span class="mr-2 inline-flex h-4 w-4 items-center md:h-5 md:w-5 lg:h-6 lg:w-6">
            <FontAwesomeIcon :icon="faRulerTriangle" />
          </span>
          {{ currentModel.floorArea }} {{ t('views.project.characteristics.floor-area') }}
        </li>
        <li v-for="tag in currentModel.tags" :key="tag.id">
          <span class="mr-2 inline-flex h-4 w-4 items-center md:h-5 md:w-5 lg:h-6 lg:w-6">
            <FontAwesomeIcon :icon="getCustomIcon(tag.customIcon)" />
          </span>
          {{ tag.name }}
        </li>
      </ul>
    </div>
  </section>
</template>

<style lang="css" scoped></style>
